import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

const useCalendarEvents = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchEvents = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {};
        if (params.calendarID) queryObj.calendarID = params.calendarID;
        if (params.termID) queryObj.termID = params.termID;
        if (params.acadyrID) queryObj.acadyrID = params.acadyrID;
        if (params.category) queryObj.category = params.category;
        if (params.eventLevel) queryObj.eventLevel = params.eventLevel;
        if (params.responsibleTeacherIDs) {
          queryObj.responsibleTeacherIDs = Array.isArray(
            params.responsibleTeacherIDs,
          )
            ? params.responsibleTeacherIDs.join(",")
            : params.responsibleTeacherIDs;
        }
        if (params.isCancelled !== undefined && params.isCancelled !== null)
          queryObj.isCancelled = params.isCancelled;
        if (params.scheduled_after)
          queryObj.scheduled_after = params.scheduled_after;
        if (params.scheduled_before)
          queryObj.scheduled_before = params.scheduled_before;

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.academics.calendarEvents}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          const data = Array.isArray(response.data)
            ? response.data
            : response.data?.results || [];
          setEvents(data);
          return data;
        } else {
          Utilities.showError(response, "Failed to fetch events");
          return [];
        }
      } catch (error) {
        message.error("Error fetching events");
        console.error(error);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const createEvent = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.academics.calendarEvents,
          data,
          navigate,
        );
        if (response.ok) {
          Utilities.showNotification(
            undefined,
            "Event created",
            "text-success",
          );
          return response.data;
        }
        Utilities.showError(response, "Failed to create event");
        return null;
      } catch (error) {
        message.error("Error creating event");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const updateEvent = useCallback(
    async (id, data) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.academics.calendarEventDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          data,
          navigate,
        );
        if (response.ok) {
          Utilities.showNotification(
            undefined,
            "Event updated",
            "text-success",
          );
          return response.data;
        }
        Utilities.showError(response, "Failed to update event");
        return null;
      } catch (error) {
        message.error("Error updating event");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const deleteEvent = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.academics.calendarEventDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          Utilities.showNotification(
            undefined,
            "Event deleted",
            "text-success",
          );
          return true;
        }
        Utilities.showError(
          response,
          response.data?.detail || "Failed to delete event",
        );
        return false;
      } catch (error) {
        message.error("Error deleting event");
        console.error(error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const cancelEvent = useCallback(
    async (id, cancellationReason) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.academics.calendarEventCancel.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { cancellationReason },
          navigate,
        );
        if (response.ok) {
          Utilities.showNotification(
            undefined,
            "Event cancelled",
            "text-success",
          );
          return response.data;
        }
        Utilities.showError(
          response,
          response.data?.detail || "Failed to cancel event",
        );
        return null;
      } catch (error) {
        message.error("Error cancelling event");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const fetchEventParticipants = useCallback(
    async (id, studentID = null) => {
      try {
        let url =
          domainSettings.urls.academics.calendarEventParticipants.replace(
            ":id",
            id,
          );
        if (studentID) url += `?studentID=${studentID}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok)
          return Array.isArray(response.data)
            ? response.data
            : response.data?.results || [];
        Utilities.showError(response, "Failed to fetch participants");
        return [];
      } catch (error) {
        message.error("Error fetching participants");
        console.error(error);
        return [];
      }
    },
    [navigate],
  );

  const fetchEventSummary = useCallback(
    async (id) => {
      try {
        const url = domainSettings.urls.academics.calendarEventSummary.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok) return response.data;
        return null;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  return {
    events,
    loading,
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    cancelEvent,
    fetchEventParticipants,
    fetchEventSummary,
  };
};

export default useCalendarEvents;
